// src/components/billing/PartnerBillingCard.jsx
import React, { useEffect, useState, useContext } from "react";
import { supabase } from "@/lib/supabaseClient";
import { ProfileContext } from "@/context/ProfileContext";
import { useTranslation } from "react-i18next";

export default function PartnerBillingCard({ partnerType }) {
  const { profile } = useContext(ProfileContext);
  const { t, i18n } = useTranslation();
  const [invoices, setInvoices] = useState([]);

  const locale = i18n.resolvedLanguage || i18n.language || "fr";

  const load = async () => {
    const { data } = await supabase
      .from("partner_invoices")
      .select("id, description, amount_cents, currency, status, due_date, issued_at, meta")
      .eq("partner_id", profile.id)
      .order("issued_at", { ascending: false });
    setInvoices(data || []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [profile?.id]);

  const formatAmount = (cents, currency = "EUR") =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "EUR",
    }).format((cents || 0) / 100);

  const formatDate = (value) =>
    value ? new Intl.DateTimeFormat(locale).format(new Date(value)) : "";

  const statusLabel = (status) =>
    t(`partnerBilling.status.${status}`, {
      defaultValue: status || t("partnerBilling.status.unknown"),
    });

  return (
    <div className="p-6 bg-white shadow rounded-lg">
      <h2 className="text-xl font-semibold mb-1">
        {t("partnerBilling.title", {
          type: t(`partnerBilling.partnerTypes.${partnerType === "transport" ? "transport" : "insurance"}`),
        })}
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        {t("partnerBilling.description")}
      </p>

      <div className="space-y-2">
        {invoices.map((inv) => (
          <div key={inv.id} className="border rounded-xl p-3 flex items-center justify-between">
            <div>
              <div className="font-medium">{inv.description || t("partnerBilling.invoiceFallback")}</div>
              <div className="text-sm text-gray-600">
                {t("partnerBilling.issuedOn", { date: formatDate(inv.issued_at) })} •{" "}
                {inv.due_date
                  ? t("partnerBilling.dueOn", { date: formatDate(inv.due_date) })
                  : t("partnerBilling.noDueDate")}
              </div>
              <div className="text-xs text-gray-500">{statusLabel(inv.status)}</div>
            </div>
            <div className="text-right">
              <div className="font-semibold">
                {formatAmount(inv.amount_cents, inv.currency || "EUR")}
              </div>
              {inv.status !== "paid" && (
                <button className="mt-1 text-sm underline">
                  {t("partnerBilling.pay")}
                </button>
              )}
            </div>
          </div>
        ))}
        {invoices.length === 0 && (
          <div className="text-sm text-gray-500">{t("partnerBilling.empty")}</div>
        )}
      </div>
    </div>
  );
}
